import React from "react";
import { useLocation } from "react-router-dom";
import { trackEvent, trackEngagementAction, consumeEngagementActions, addSessionEngagedMs, touchSessionActivity } from "../lib/analytics";

// Tracks per-page engagement: aggregates actions (scroll, clicks) and visible time
export default function useEngagementTracking() {
  const location = useLocation();
  const pathname = location.pathname;

  React.useEffect(() => {
    let engagedMs = 0;
    let lastVisibleSince = Date.now();
    const firedMilestones = new Set();
    const milestones = [25, 50, 75, 100];
    let flushTimer = null;

    function scheduleFlush(delay = 30000) {
      clearTimeout(flushTimer);
      flushTimer = setTimeout(() => {
        const actions = consumeEngagementActions(pathname);
        if (actions.length > 0 || engagedMs >= 1000) {
          trackEvent("Page Engagement", {
            pathname,
            engaged_seconds: Math.round(engagedMs / 1000),
            max_scroll_percent: Math.max(0, ...Array.from(firedMilestones)),
            actions,
          });
        }
        scheduleFlush(30000);
      }, delay);
    }

    function updateEngagedTime() {
      if (document.visibilityState === "visible") {
        const now = Date.now();
        const delta = now - lastVisibleSince;
        engagedMs += delta;
        lastVisibleSince = now;
        try { addSessionEngagedMs(delta); } catch (_) {}
        try { touchSessionActivity(); } catch (_) {}
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "hidden") {
        updateEngagedTime();
        // immediate flush on hide
        const actions = consumeEngagementActions(pathname);
        if (actions.length > 0 || engagedMs >= 1000) {
          trackEvent("Page Engagement", {
            pathname,
            engaged_seconds: Math.round(engagedMs / 1000),
            max_scroll_percent: Math.max(0, ...Array.from(firedMilestones)),
            actions,
          });
        }
      } else {
        lastVisibleSince = Date.now();
      }
    }

    function handleNewAction() {
      // small debounce: flush soon so you can see actions without navigating away
      scheduleFlush(1000);
    }

    function handleScroll() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
      const viewport = window.innerHeight || document.documentElement.clientHeight || 0;
      const full = document.documentElement.scrollHeight || document.body.scrollHeight || 0;

      const scrollable = Math.max(full - viewport, 0);
      if (scrollable <= 0) return;

      const progress = Math.min(100, Math.max(0, Math.round((scrollTop / scrollable) * 100)));
      for (const m of milestones) {
        if (progress >= m && !firedMilestones.has(m)) {
          firedMilestones.add(m);
          // buffer as engagement action instead of firing event immediately
          trackEngagementAction("scroll_depth", { percent: m, pathname });
        }
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    // Ensure engagement flush happens before session end
    const handleSessionWillEnd = () => {
      updateEngagedTime();
      const actions = consumeEngagementActions(pathname);
      if (engagedMs >= 1000 || actions.length > 0) {
        trackEvent("Page Engagement", {
          pathname,
          engaged_seconds: Math.round(engagedMs / 1000),
          max_scroll_percent: Math.max(0, ...Array.from(firedMilestones)),
          actions,
        });
      }
    };
    window.addEventListener('mp-session-will-end', handleSessionWillEnd);
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("mp-engagement-action", handleNewAction);
    try { touchSessionActivity(); } catch (_) {}

    // start periodic flush every 30s so long sessions are captured
    scheduleFlush(30000);

    return () => {
      clearTimeout(flushTimer);
      updateEngagedTime();
      const actions = consumeEngagementActions(pathname);
      if (engagedMs >= 1000 || actions.length > 0) {
        trackEvent("Page Engagement", {
          pathname,
          engaged_seconds: Math.round(engagedMs / 1000),
          max_scroll_percent: Math.max(0, ...Array.from(firedMilestones)),
          actions, // array of buffered actions during this view
        });
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener('mp-session-will-end', handleSessionWillEnd);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mp-engagement-action", handleNewAction);
    };
  }, [pathname]);
}
